import React from "react";
import {
  Box,
  Button,
  Divider,
  Grid2,
  InputAdornment,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Trans, useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import EmailIcon from "@icons/myicons/email.svg?react";
import { Modal } from "@rentablo/components";
import { Banner } from "@rentablo/components";
import { LoadingButton } from "@rentablo/components";
import { FormField } from "@helpers";
import { renderFormElement } from "@helpers/renderFormElement";
import { useInitiateEmailChange } from "@rentablo/api/apiv1";

const formFields: FormField[] = [
  {
    name: "oldEmail",
    type: "email",
  },
  {
    name: "newEmail",
    type: "email",
  },
  {
    name: "newEmailConfirmation",
    type: "email",
  },
];

type Props = {
  onClose: () => void;
  isOpen: boolean;
  email: string;
};

export const EmailEditModal = ({ email, onClose, isOpen }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { mutate: changeEmail, isPending } = useInitiateEmailChange();
  const [showSuccessBanner, setShowSuccessBanner] = React.useState(false);
  const isDownMd = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  const schema = Yup.object().shape({
    oldEmail: Yup.string()
      .email(t("form.errors.wrongEmailFormat"))
      .required(t("form.errors.required")),
    newEmail: Yup.string()
      .email(t("form.errors.wrongEmailFormat"))
      .required(t("form.errors.required")),
    newEmailConfirmation: Yup.string()
      .email(t("form.errors.wrongEmailFormat"))
      .required(t("form.errors.required"))
      .test(
        "emails-match",
        t("form.errors.emailsNotMatched"),
        function (value) {
          return this.parent.newEmail === value;
        }
      ),
  });

  const { control, handleSubmit, setError, watch } = useForm({
    mode: "onSubmit",
    resolver: yupResolver(schema),
    defaultValues: {
      oldEmail: email,
      newEmail: "",
      newEmailConfirmation: "",
    },
  });

  const newEmail = watch("newEmail");

  const onSubmit = (data: any) => {
    changeEmail(
      {
        email: data.newEmail,
      },
      {
        onError: (error) => {
          if (error.response?.status === 409) {
            setError("newEmail", {
              type: "manual",
              message: t("form.errors.emailAlreadyExists"),
            });
          } else {
            enqueueSnackbar(t("form.errors.somethingWentWrong"), {
              variant: "error",
            });
          }
        },
        onSuccess: () => {
          setShowSuccessBanner(true);
        },
      }
    );
  };

  return (
    <Modal
      title={t("settings.profile.emailEditModal.title")}
      subtitle={t("settings.profile.emailEditModal.subtitle")}
      isOpen={isOpen}
      onClose={onClose}
      height="auto"
      width={600}
    >
      {showSuccessBanner ? (
        <Box>
          <Banner
            type="info"
            text={
              <Trans
                i18nKey="settings.profile.emailEditModal.success"
                values={{
                  email: newEmail,
                }}
                components={{
                  strongText: <Typography variant="body4" component="span" />,
                }}
              />
            }
            mb={6}
          />
          <Box display="flex" justifyContent="flex-end">
            <Button variant="outlined" onClick={onClose}>
              {t("settings.profile.emailEditModal.close")}
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          <Grid2 container spacing={6} mb={11}>
            {formFields.map((field) => {
              if (field.name === "oldEmail") {
                return (
                  <Grid2
                    key={field.name}
                    size={{
                      xs: 12,
                      md: 12,
                    }}
                  >
                    {renderFormElement(
                      "settings.profile.form",
                      field,
                      control,
                      {
                        InputProps: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon />
                            </InputAdornment>
                          ),
                        },
                        disabled: true,
                      }
                    )}
                    <Divider sx={{ marginTop: theme.spacing(6) }} />
                  </Grid2>
                );
              }
              return (
                <Grid2
                  key={field.name}
                  size={{
                    xs: 12,
                    md: 12,
                  }}
                >
                  {renderFormElement("settings.profile.form", field, control, {
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    },
                  })}
                </Grid2>
              );
            })}
          </Grid2>
          <Box
            display="flex"
            justifyContent="flex-end"
            gap={4}
            pb={2}
            flexDirection={isDownMd ? "column" : "row"}
          >
            <Button variant="outlined" onClick={onClose} fullWidth={isDownMd}>
              {t("settings.profile.emailEditModal.close")}
            </Button>
            <LoadingButton
              variant="primary"
              isFetching={isPending}
              onClick={handleSubmit(onSubmit)}
              fullWidth={isDownMd}
            >
              {t("settings.profile.emailEditModal.submit")}
            </LoadingButton>
          </Box>
        </>
      )}
    </Modal>
  );
};
